// src/context/brandVoice/ui/SaveBar.jsx — v183's SaveArea, now live.
//
// ONE bar for the whole screen, because ONE "Salva modifiche" covers both halves (the
// guidelines document and the brand personality) even though they are two writes with
// two etags: what the user changed is one thought, and per-tab save buttons would hide
// that a change on another tab is still pending.
// ⛔ It decides nothing and calls no API: `count`, `locked` and `saving` are handed in,
// and `messages` are already OUR sentences (`copy.js`) — a server sentence never reaches
// this component.
// ⭐ "Annulla" asks first: the drafts it drops are the only copy of the user's text.
// v200: no "Aggiorna anche l'inglese" switch any more — every save translates.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import ConfirmDialog from '../../../admin/components/ConfirmDialog.jsx';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../../constants/theme.js';

const TONES = { success: SEMANTIC.verde, warning: SEMANTIC.attenzione, error: SEMANTIC.errore };
const BTN_BASE = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px',
  borderRadius: RADII.control, fontFamily: FONT_SANS, fontSize: 13, fontWeight: 600,
};

export default function SaveBar({
  count = 0, saving = false, locked = false, onCancel, onSave, messages,
}) {
  const { t } = useTranslation('context');
  const [confirming, setConfirming] = useState(false);
  const canCancel = count > 0 && !saving;
  const canSave = count > 0 && !saving && !locked;
  const list = Array.isArray(messages) ? messages : [];

  return (
    <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: '16px 24px 24px', fontFamily: FONT_SANS }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, marginBottom: 16,
      }}>
        <span style={{ fontSize: 13, color: count > 0 ? COLORS.ink : COLORS.textTertiary }}>
          {count > 0 ? t('brandVoice.save.count', { count }) : t('brandVoice.save.none')}
        </span>
      </div>

      {list.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {list.map((m, i) => {
            const sem = TONES[m.tone] || SEMANTIC.info;
            return (
              <div
                key={`${m.tone}-${i}`}
                role={m.tone === 'error' ? 'alert' : 'status'}
                style={{
                  padding: '10px 14px', borderRadius: RADII.control,
                  background: sem.bg, color: sem.fg, fontSize: 13, lineHeight: '19px',
                }}
              >
                <span>{m.text}</span>
                {m.action && (
                  <button
                    type="button" onClick={m.action.onClick}
                    style={{
                      marginLeft: 10, padding: '4px 10px', borderRadius: RADII.controlInner,
                      border: `1px solid ${sem.fg}`, background: 'transparent', color: sem.fg,
                      fontFamily: FONT_SANS, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {m.action.label}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          type="button" disabled={!canCancel} onClick={() => setConfirming(true)}
          style={{
            ...BTN_BASE, border: `1px solid ${canCancel ? COLORS.borderStrong : COLORS.border}`,
            background: COLORS.surface, color: canCancel ? COLORS.ink : COLORS.textTertiary,
            cursor: canCancel ? 'pointer' : 'not-allowed',
          }}
        >
          {t('brandVoice.save.cancel')}
        </button>
        <button
          type="button" disabled={!canSave} onClick={() => onSave?.()}
          style={{
            ...BTN_BASE, border: 'none', color: COLORS.surface,
            background: canSave ? COLORS.accent : COLORS.borderStrong,
            cursor: canSave ? 'pointer' : 'not-allowed',
          }}
        >
          <Save size={16} strokeWidth={1.8} aria-hidden="true" />
          {saving ? t('brandVoice.save.saving') : t('brandVoice.save.submit')}
        </button>
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => onCancel?.()}
        title={t('brandVoice.save.discardTitle')}
        message={t('brandVoice.save.discardMessage')}
        // ⛔ No `confirmLabel`: ConfirmDialog's own dismiss button already reads
        // "Annulla", so labelling the CONFIRM "Annulla" too would put two buttons with
        // one word and opposite meanings side by side. The default "Conferma" answers
        // the title ("Annullare le modifiche?") unambiguously.
        variant="danger"
      />
    </div>
  );
}
